/*
 * InputBoxV3 — caixa oficial de input da view Inputs V3.
 *
 * Concepção visual (donut consolidado):
 *   - Cartão vertical compacto (largura fixa).
 *   - Donut envolvendo um ícone de "natureza" (arquivo, semi-fixo, conexão).
 *     O preenchimento do donut representa a qualidade da informação,
 *     medida pela COBERTURA DOS CRUZAMENTOS desse input com os demais.
 *   - Score em % sobreposto ao canto inferior-direito do donut.
 *   - Nome do input + responsável por extenso, centralizados.
 *   - Rodapé com três "risquinhos" sutis (Origem · Estrutura · Cruzamentos).
 *   - Ponto discreto no canto superior direito quando o input está sob
 *     responsabilidade do usuário atual.
 *   - Tooltip rico no hover, explicando o significado de cada risquinho
 *     e trazendo metadados operacionais.
 *
 * O componente consome a mesma shape V2 usada pela InputsV3.js (vinda do
 * inputsV3Adapter.js), derivando internamente `quality_score`, mensagens
 * de estágio e natureza a partir dos dados já disponíveis.
 */

import React, { useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import {
  InsertDriveFile,
  Tune,
  Sync,
  Repeat,
  FlashOn,
  PersonOutline,
  AttachFile,
  Event,
  HelpOutline,
  TrendingFlat,
  CloudUpload,
} from "@material-ui/icons";
import {
  dangerColor,
  grayColor,
  successColor,
  warningColor,
  whiteColor,
} from "../../../assets/jss/material-dashboard-pro-react";
import {
  INPUT_STATE_COLORS as STATE_COLORS,
  INPUT_STATE_BACKGROUNDS as STATE_BACKGROUNDS,
  PROCESSING_COLORS,
  inputStateLabel,
  classifyInputAggregate,
  classifyInputPip,
  inputSinglePipState,
} from "../../../services/inputsV3StateRules";
import {
  isCarryForward,
  isSingleton,
  isYearly,
} from "../../../services/inputsV3Volatility";
import StagePips, {
  IN_FLIGHT_RIPPLE_DURATION_MS,
  lightenInFlightColor,
} from "./StagePips";
import useFileDropZone from "./hooks/useFileDropZone";

// ----------------------------------------------------------------------
// Constantes visuais
// ----------------------------------------------------------------------

// Em uma área de conteúdo de ~1680px com gap de 12px, permite ~8 cards
// lado-a-lado. Exposto caso a view pai precise calcular limites.
export const INPUT_BOX_V3_MIN_WIDTH = 110;
export const INPUT_BOX_V3_MAX_WIDTH = 168;

// ----------------------------------------------------------------------
// Helpers visuais derivados da fonte canonica (inputsV3StateRules.js).
// ----------------------------------------------------------------------
//
// Buckets executivos, color code (D-33) e labels canonicos vivem em
// ``services/inputsV3StateRules.js`` (PA-006). Aqui deixamos apenas as
// constantes locais e os helpers visuais que dependem dessa cor base
// (RUNNING_STRIPE_BG, stageColor, stageBackground).
const PROCESSING_GRAY = PROCESSING_COLORS.base;
const PROCESSING_GRAY_ALT = PROCESSING_COLORS.highlight;
const PENDING_DECISION_ORANGE = STATE_COLORS.pending_decision;

const stageColor = (status) => {
  if (status === "success") return successColor[3];
  if (status === "warning") return warningColor[2];
  // ``system_error`` (falha tecnica do pipeline) ganha tom proprio
  // (preto) — alinhado ao bucket ``system_error`` em
  // ``inputsV3StateRules`` e a ``COLORS.system_error`` em ``StagePips``.
  // ``error`` (cliente rejeitou) mantem o vermelho operacional.
  if (status === "system_error") return STATE_COLORS.system_error;
  if (status === "error") return dangerColor[2];
  if (status === "pending_decision") return PENDING_DECISION_ORANGE;
  // Onda 3.1: ``processing`` e' o nome canonico (era ``running``).
  if (status === "processing" || status === "running") return grayColor[5];
  return grayColor[5];
};

// Onda 1.3 (convergencia processing pip<->donut<->badge): trocamos o
// ``repeating-linear-gradient`` 135deg (listras barber-pole) por um
// ``linear-gradient`` horizontal ``base -> luz -> base`` — mesmo
// perfil de ripple usado em ``StagePips.pipBackgroundForInFlight`` e
// no chip "Processando" do header. O badge agora respira a mesma
// onda que o donut e o pip, em vez de mostrar listras enquanto os
// outros mostram shimmer. ``scoreBadgeRunning`` (regra CSS abaixo)
// anima ``backgroundPosition`` com ``backgroundSize: 200% 100%``.
const RUNNING_STRIPE_BG = `linear-gradient(
  90deg,
  ${PROCESSING_GRAY} 0%,
  ${PROCESSING_GRAY_ALT} 50%,
  ${PROCESSING_GRAY} 100%
)`;

const stageBackground = (status) =>
  // Onda 3.1: ``processing`` substituiu ``running`` no enum canonico.
  status === "processing" || status === "running"
    ? RUNNING_STRIPE_BG
    : stageColor(status);

function displayStageStatus(input, stageKey) {
  const status = input?.stages?.[stageKey];
  if (
    stageKey === "cross_linking" &&
    input?.cross_linking == null &&
    (input?.stages?.data_structure === "success" ||
      input?.stages?.data_structure === "warning")
  ) {
    return "success";
  }
  return status;
}

// Wrapper backward-compat: ``classifyInputState`` continua sendo
// exportada deste modulo para nao quebrar imports legados (ex.:
// ``DrawerHeader`` ainda fazia ``import { classifyInputState } from
// "../InputBoxV3"``). A logica real vive em
// ``services/inputsV3StateRules.js`` — fonte unica de verdade (PA-006).
export const classifyInputState = classifyInputAggregate;

// Score do donut = cobertura real de cross-link publicada pelo runtime
// V3 (campo ``quality_score`` no payload do input).
//
// Semantica de "100%":
//   "100% dos cruzamentos do input fecharam sem divergencia NO MES
//    CORRENTE."
//
// Regra unica: so emitimos 100 quando o input EFETIVAMENTE participa
// de cruzamentos E todos terminaram em ``success`` para o periodo
// selecionado. Demais situacoes caem em ``null`` -> badge "n/a":
//   - input sem cruzamentos declarados (``cross_linking == null``):
//     nao existe medida de cobertura — nao ha o que cruzar;
//   - cruzamentos declarados mas ainda nao executados (running/
//     pending/blocked): a medida ainda nao e conhecida;
//   - warning/error: ha divergencia; o numero real depende da
//     metrica do executor (que so virara via ``quality_score``).
//
// IMPORTANTE: ``is_permanent_for_month`` (input semi-fixo herdado do
// mes anterior) NAO e mais um atalho para 100. Um de-para reusado pode
// ter cobertura incompleta no mes corrente (ex.: produto novo apareceu
// nas vendas e nao esta no de-para). A cobertura tem que vir do estado
// do cross-link calculado contra os dados transacionais do mes — nao
// do fato de o arquivo ter sido reaproveitado.
function deriveQualityScore(input) {
  const raw = input?.quality_score;
  if (raw != null) {
    const n = Number(raw);
    if (Number.isFinite(n)) return Math.max(0, Math.min(100, Math.round(n)));
  }
  const xl = input?.cross_linking || null;
  if (xl && xl.state === "success") return 100;
  return null;
}

function deriveNature(input) {
  if (input?.nature) return input.nature;
  if (input?.source === "auto_generated") return "semifixed";
  return "file";
}

function natureIconEl(nature, style) {
  switch (nature) {
    case "semifixed":
      return <Tune style={style} />;
    case "connection":
      return <Sync style={style} />;
    case "file":
    default:
      return <InsertDriveFile style={style} />;
  }
}

// Lista de responsaveis canonicos (nao o uploader). Considera tanto
// a shape V3 (`responsible_users: [{name, email, is_current_user}]`)
// quanto a V2 (`responsible: [{name, ...}]`). Desde a Frente 1 da
// varredura V3, NAO caimos mais em ``updated_by_username`` como
// fallback — aquele campo reflete quem SUBIU o arquivo, e confundir
// uploader com responsavel era exatamente o bug corrigido.
function responsibleList(input) {
  if (
    Array.isArray(input?.responsible_users) &&
    input.responsible_users.length > 0
  ) {
    return input.responsible_users;
  }
  if (Array.isArray(input?.responsible) && input.responsible.length > 0) {
    return input.responsible;
  }
  return [];
}

// ----------------------------------------------------------------------
// Mensagens por estágio (alimentam o tooltip)
// ----------------------------------------------------------------------

// Labels das 3 linhas de "Verificacoes" no tooltip.
//
// Design (user feedback 2026-04-23):
//   - O LABEL e o ASSUNTO da etapa (estavel em qualquer status): o que
//     o pipeline olha naquele passo. Nao carrega resultado — evita
//     contradicoes do tipo "Estrutura validada" exibido junto com
//     "Problemas na estrutura".
//   - O COMPLEMENTO (``stageMessage``) e a unica linha de status, curta
//     e alinhada a cor da barra do dash.
//   - Nao ha mais ``hint`` separado. Antes, o hint fixo ("Colunas e
//     formatos conforme o esperado") aparecia mesmo quando havia
//     problema — leitura dissonante. Toda a narrativa agora vive no
//     label + complemento, sem texto redundante.
// Labels: ``label`` permanece como fallback PT-BR para callsites que
// nao tem ``t`` (raros). O callsite principal — ``TooltipBody`` —
// resolve o label via ``t(stageLabelKey)`` para localizar pt/en/es.
const STAGE_DEFS = [
  {
    key: "data_source",
    label: "Envio da informação",
    labelKey: "inputs_v3.input_tooltip.stage_data_source",
  },
  {
    key: "data_structure",
    label: "Análise da estrutura",
    labelKey: "inputs_v3.input_tooltip.stage_data_structure",
  },
  {
    key: "cross_linking",
    label: "Integridade entre dados",
    labelKey: "inputs_v3.input_tooltip.stage_cross_linking",
  },
];

function stageMessage(input, stageKey, t) {
  const status = input?.stages?.[stageKey];
  const isPermanent = !!input?.is_permanent_for_month;

  // ``tx(key, fallback, vars)``: dispara ``t`` quando disponivel,
  // senao cai no fallback PT-BR. Pre-existia inline em cada bloco —
  // agora centralizado para reduzir ruido.
  const tx = (key, fallback, vars) => {
    if (typeof t === "function") {
      return vars
        ? t(key, { defaultValue: fallback, ...vars })
        : t(key, fallback);
    }
    return fallback;
  };

  if (stageKey === "data_source") {
    if (isPermanent) {
      return tx(
        "inputs_v3.input_tooltip.data_source.permanent",
        "Informação contínua",
      );
    }
    if (status === "success") {
      const when = input?.updated_in;
      const who = input?.updated_by_username;
      if (when && who) {
        return tx(
          "inputs_v3.input_tooltip.data_source.received_at_by",
          `Recebido em ${when} por ${who}`,
          { when, who },
        );
      }
      if (when) {
        return tx(
          "inputs_v3.input_tooltip.data_source.received_at",
          `Recebido em ${when}`,
          { when },
        );
      }
      if (who) {
        return tx(
          "inputs_v3.input_tooltip.data_source.received_by",
          `Recebido por ${who}`,
          { who },
        );
      }
      return tx("inputs_v3.input_tooltip.data_source.received", "Recebido");
    }
    if (status === "processing" || status === "running") {
      return tx(
        "inputs_v3.input_tooltip.data_source.processing",
        "Envio em andamento…",
      );
    }
    return tx(
      "inputs_v3.input_tooltip.data_source.waiting",
      "Aguardando envio",
    );
  }

  if (stageKey === "data_structure") {
    if (status === "success") {
      const hasObs = Number(input?.observation_count || 0) > 0;
      return hasObs
        ? tx(
            "inputs_v3.input_tooltip.data_structure.success_with_observations",
            "Sem observações críticas",
          )
        : tx(
            "inputs_v3.input_tooltip.data_structure.success",
            "Estrutura conforme o esperado",
          );
    }
    if (status === "warning") {
      return tx(
        "inputs_v3.input_tooltip.data_structure.warning",
        "Estrutura com ressalvas",
      );
    }
    if (status === "system_error") {
      return tx(
        "inputs_v3.input_tooltip.data_structure.system_error",
        "Erro de processamento",
      );
    }
    if (status === "error") {
      return tx(
        "inputs_v3.input_tooltip.data_structure.error",
        "Problemas na estrutura do arquivo",
      );
    }
    if (status === "pending_decision") {
      return tx(
        "inputs_v3.input_tooltip.data_structure.pending_decision",
        "Aguardando decisão",
      );
    }
    if (status === "processing" || status === "running") {
      return tx(
        "inputs_v3.input_tooltip.data_structure.processing",
        "Análise em andamento…",
      );
    }
    return tx(
      "inputs_v3.input_tooltip.data_structure.waiting",
      "Aguardando análise",
    );
  }

  if (stageKey === "cross_linking") {
    const xl = input?.cross_linking || null;
    if (xl == null && status !== "processing" && status !== "running") {
      return tx(
        "inputs_v3.input_tooltip.cross_linking.no_crosslinks",
        "Dado não contém cruzamentos",
      );
    }
    if (status === "success") {
      return tx(
        "inputs_v3.input_tooltip.cross_linking.success",
        "Integridade conforme a esperada",
      );
    }
    if (status === "partial") {
      const total = Number(xl?.count || 0);
      const done = Array.isArray(xl?.clean_completed_names)
        ? xl.clean_completed_names.length
        : 0;
      if (total > 0 && done > 0) {
        return tx(
          "inputs_v3.input_tooltip.cross_linking.partial_progress",
          `Integridade parcial — ${done} de ${total} cruzamentos verificados sem apontamentos`,
          { done, total },
        );
      }
      return tx(
        "inputs_v3.input_tooltip.cross_linking.partial",
        "Integridade parcial — verificações em andamento",
      );
    }
    if (status === "warning") {
      return tx(
        "inputs_v3.input_tooltip.cross_linking.warning",
        "Integridade com pontos de atenção",
      );
    }
    if (status === "system_error") {
      return tx(
        "inputs_v3.input_tooltip.cross_linking.system_error",
        "Erro de processamento",
      );
    }
    if (status === "error" || status === "pending_decision") {
      return tx(
        "inputs_v3.input_tooltip.cross_linking.error",
        "Integridade com pontos críticos",
      );
    }
    if (status === "processing" || status === "running") {
      return tx(
        "inputs_v3.input_tooltip.cross_linking.processing",
        "Verificação em andamento…",
      );
    }
    return tx(
      "inputs_v3.input_tooltip.cross_linking.waiting",
      "Aguardando verificação de integridade",
    );
  }

  return "";
}

function uploadMeta(input) {
  // ``updated_by_username`` agora reflete EXCLUSIVAMENTE o uploader
  // (``InputControlV3.updated_by``). Se o backend nao registrou
  // quem subiu, ``who`` fica ``null`` e o tooltip mostra a linha
  // "Enviado em <data>" sem atribuicao — NAO cai no responsavel.
  const who = input?.updated_by_username || null;
  const when = input?.updated_in || null;
  let kind = "file";
  if (input?.source === "auto_generated") {
    kind = deriveNature(input) === "connection" ? "auto" : "ui";
  }
  return { who, when, kind };
}

// ----------------------------------------------------------------------
// Donut (SVG)
// ----------------------------------------------------------------------

const Donut = ({
  progress,
  color,
  size,
  thickness,
  dashed,
  striped,
  inFlight,
}) => {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const safeProgress = Math.max(0, Math.min(1, progress || 0));
  const dash = circumference * safeProgress;
  // I-22: id unico por instancia (multiplos donuts no DOM nao podem
  // compartilhar <linearGradient>). Onda 1.3: removido o
  // ``patternId`` legado — agora ``striped`` reusa o mesmo gradiente
  // do ``inFlight`` (so trocando a cor base), entao basta um id.
  const inFlightGradIdRef = useRef(
    `inputbox-v3-inflight-grad-${Math.random().toString(36).slice(2, 11)}`,
  );
  const inFlightGradId = inFlightGradIdRef.current;

  // Onda 1.3 (convergencia processing pip<->donut<->badge): tanto
  // ``striped`` (state=processing) quanto ``inFlight`` (xlinks em
  // fila) agora usam o MESMO ``<linearGradient>`` com 3 stops
  // ``base -> luz -> base`` animado horizontalmente — ripple/onda
  // identico ao chip "Processando" do header e ao pip do StagePips.
  // Antes ``striped`` usava um ``<pattern>`` 45deg (barber pole) e
  // ``inFlight`` usava o gradiente ripple, fazendo o donut de um
  // input em processamento ter aspecto diferente do donut de um
  // input com xlink em fila — ambos sao "alguma coisa rodando" do
  // ponto de vista do consultor. So a COR base muda:
  //   - ``striped``: cinza (PROCESSING_GRAY) — pipeline tecnico em
  //     curso, sem informacao de qualidade ainda.
  //   - ``inFlight``: cor do estado da Camada 1 (verde claro,
  //     amarelo, etc.) — ja tem veredito + tem coisa rodando.
  // ``striped`` continua tendo prioridade visual quando ambos sao
  // verdadeiros (pipeline rodando supera xlink em fila).
  const showRipple = striped || inFlight;
  const baseColor = striped ? PROCESSING_GRAY : color;
  const lighter = striped
    ? PROCESSING_GRAY_ALT
    : showRipple
      ? lightenInFlightColor(color)
      : null;

  return (
    <svg width={size} height={size}>
      <defs>
        {showRipple && (
          <linearGradient
            id={inFlightGradId}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={baseColor}>
              <animate
                attributeName="offset"
                values="-1;0;1"
                dur={`${IN_FLIGHT_RIPPLE_DURATION_MS}ms`}
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="50%" stopColor={lighter}>
              <animate
                attributeName="offset"
                values="-0.5;0.5;1.5"
                dur={`${IN_FLIGHT_RIPPLE_DURATION_MS}ms`}
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor={baseColor}>
              <animate
                attributeName="offset"
                values="0;1;2"
                dur={`${IN_FLIGHT_RIPPLE_DURATION_MS}ms`}
                repeatCount="indefinite"
              />
            </stop>
          </linearGradient>
        )}
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={grayColor[5]}
        strokeWidth={thickness}
        strokeDasharray={dashed ? "4 4" : undefined}
      />
      {safeProgress > 0 && (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={showRipple ? `url(#${inFlightGradId})` : color}
          strokeWidth={thickness}
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeDashoffset={circumference / 4}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dasharray 0.3s" }}
        />
      )}
    </svg>
  );
};
Donut.propTypes = {
  progress: PropTypes.number,
  color: PropTypes.string.isRequired,
  size: PropTypes.number,
  thickness: PropTypes.number,
  dashed: PropTypes.bool,
  striped: PropTypes.bool,
  inFlight: PropTypes.bool,
};
Donut.defaultProps = {
  size: 68,
  thickness: 6,
  dashed: false,
  striped: false,
  inFlight: false,
};

// ----------------------------------------------------------------------
// Tooltip rico (título + modificadores de viewport)
// ----------------------------------------------------------------------

const useTooltipStyles = makeStyles(() => ({
  tooltip: {
    backgroundColor: whiteColor,
    color: grayColor[2],
    boxShadow: "0 4px 14px rgba(0,0,0,0.14)",
    border: "1px solid " + grayColor[5],
    maxWidth: "360px",
    padding: "14px 16px",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  arrow: {
    color: whiteColor,
    "&::before": { border: "1px solid " + grayColor[5] },
  },
}));

const useTitleStyles = makeStyles(() => ({
  headerRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "8px",
    marginBottom: "2px",
  },
  title: { fontSize: "13px", fontWeight: 700, color: grayColor[2] },
  score: { fontSize: "15px", fontWeight: 700 },
  subtitle: { fontSize: "11px", color: grayColor[0], marginBottom: "10px" },
  stateLine: { fontSize: "11px", color: grayColor[0], marginBottom: "12px" },
  sectionLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: grayColor[0],
    marginBottom: "6px",
  },
  stagesWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    paddingBottom: "12px",
    marginBottom: "12px",
    borderBottom: "1px dashed " + grayColor[5],
  },
  stageRow: { display: "flex", alignItems: "flex-start", gap: "10px" },
  stageBar: {
    width: "18px",
    height: "6px",
    borderRadius: "3px",
    marginTop: "5px",
    flexShrink: 0,
  },
  stageBody: { flex: 1, minWidth: 0 },
  stageLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: grayColor[2],
    lineHeight: 1.25,
  },
  stageMessage: { fontSize: "11px", color: grayColor[0], lineHeight: 1.35 },
  metaWrap: { display: "flex", flexDirection: "column", gap: "4px" },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    color: grayColor[0],
  },
  metaLabel: { color: grayColor[0] },
  metaValue: { color: grayColor[2], fontWeight: 500 },
  // Afforda­nce discreta no rodapé do tooltip sinalizando que o cartão é
  // clicável e abre o drawer com detalhes completos.
  clickHint: {
    marginTop: "8px",
    paddingTop: "6px",
    borderTop: "1px solid " + grayColor[8],
    fontSize: "10px",
    fontStyle: "italic",
    color: grayColor[1],
    textAlign: "center",
  },
}));

const TooltipBody = ({ input }) => {
  const c = useTitleStyles();
  const { t } = useTranslation();
  const state = classifyInputState(input);
  const color = STATE_COLORS[state];
  const score = deriveQualityScore(input);
  const scoreStr = score == null ? "n/a" : score + "%";
  const meta = uploadMeta(input);
  const respList = responsibleList(input);

  // Label "Enviado por / Sincronizado por / Editado por" baseado em
  // ``meta.kind``. Centralizamos pra reaproveitar tanto a forma "por"
  // (com uploader) quanto a forma "em" (sem uploader).
  const uploadVerbBy = () => {
    if (meta.kind === "auto") {
      return t("inputs_v3.input_tooltip.meta.synced_by", "Sincronizado por");
    }
    if (meta.kind === "ui") {
      return t("inputs_v3.input_tooltip.meta.edited_by", "Editado por");
    }
    return t("inputs_v3.input_tooltip.meta.sent_by", "Enviado por");
  };
  const uploadVerbAt = () => {
    if (meta.kind === "auto") {
      return t("inputs_v3.input_tooltip.meta.synced_at", "Sincronizado em");
    }
    if (meta.kind === "ui") {
      return t("inputs_v3.input_tooltip.meta.edited_at", "Editado em");
    }
    return t("inputs_v3.input_tooltip.meta.sent_at", "Enviado em");
  };
  const responsibleLabel = (n) =>
    n === 1
      ? t("inputs_v3.input_tooltip.meta.responsible_one", "Responsável")
      : t("inputs_v3.input_tooltip.meta.responsible_other", "Responsáveis");

  return (
    <div>
      <div className={c.headerRow}>
        <div className={c.title}>
          {t("inputs_v3.input_tooltip.title", "Qualidade da informação")}
        </div>
        <div className={c.score} style={{ color }}>
          {scoreStr}
        </div>
      </div>
      <div className={c.subtitle}>
        {t(
          "inputs_v3.input_tooltip.subtitle",
          "Pior cobertura avaliada dos cruzamentos deste input com os demais.",
        )}
      </div>
      <div className={c.stateLine}>
        {t("inputs_v3.input_tooltip.current_state", "Estado atual")}:{" "}
        <strong style={{ color }}>{inputStateLabel(state, t)}</strong>
      </div>

      <div className={c.sectionLabel}>
        {t("inputs_v3.input_tooltip.section_checks", "Verificações")}
      </div>
      <div className={c.stagesWrap}>
        {STAGE_DEFS.map((def) => (
          <div key={def.key} className={c.stageRow}>
            <div
              className={c.stageBar}
              style={{
                background: stageBackground(displayStageStatus(input, def.key)),
              }}
            />
            <div className={c.stageBody}>
              <div className={c.stageLabel}>
                {def.labelKey ? t(def.labelKey, def.label) : def.label}
              </div>
              <div className={c.stageMessage}>
                {stageMessage(input, def.key, t)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={c.metaWrap}>
        {input?.updated_in == null && !input?.is_permanent_for_month ? (
          <>
            <div className={c.metaRow}>
              <AttachFile style={{ fontSize: 13 }} />
              <span className={c.metaLabel}>
                {t(
                  "inputs_v3.input_tooltip.meta.not_sent_yet",
                  "Arquivo ainda não enviado",
                )}
              </span>
            </div>
            {respList.length > 0 && (
              <div className={c.metaRow}>
                <PersonOutline style={{ fontSize: 13 }} />
                <span className={c.metaLabel}>
                  {responsibleLabel(respList.length)}
                </span>
                <span className={c.metaValue}>
                  {respList.map((r) => r.name).join(", ")}
                </span>
              </div>
            )}
          </>
        ) : meta.who || meta.when ? (
          <>
            <div className={c.metaRow}>
              <PersonOutline style={{ fontSize: 13 }} />
              {meta.who ? (
                <>
                  <span className={c.metaLabel}>{uploadVerbBy()}</span>
                  <span className={c.metaValue}>{meta.who}</span>
                  {meta.when && (
                    <>
                      <span className={c.metaLabel}>
                        {t("inputs_v3.input_tooltip.meta.at", "em")}
                      </span>
                      <span className={c.metaValue}>{meta.when}</span>
                    </>
                  )}
                </>
              ) : (
                <>
                  <span className={c.metaLabel}>{uploadVerbAt()}</span>
                  <span className={c.metaValue}>{meta.when}</span>
                </>
              )}
            </div>
            {respList.length >= 1 && (
              <div className={c.metaRow}>
                <PersonOutline style={{ fontSize: 13 }} />
                <span className={c.metaLabel}>
                  {responsibleLabel(respList.length)}
                </span>
                <span className={c.metaValue}>
                  {respList.map((r) => r.name).join(", ")}
                </span>
              </div>
            )}
          </>
        ) : (
          respList.length > 0 && (
            <div className={c.metaRow}>
              <span className={c.metaLabel}>
                {responsibleLabel(respList.length)}:{" "}
                <span className={c.metaValue}>
                  {respList.map((r) => r.name).join(", ")}
                </span>
              </span>
            </div>
          )
        )}
      </div>
      <div className={c.clickHint}>
        {t("inputs_v3.input_tooltip.click_hint", "Clique para mais detalhes")}
      </div>
    </div>
  );
};
TooltipBody.propTypes = { input: PropTypes.object.isRequired };

// Modificadores do Popper.
//
// Ponto critico: `flip` DESLIGADO. O flip automatico do popper e a causa
// do flickering — quando o tooltip esta perto das bordas, o popper pode
// oscilar entre top/bottom/right/left porque cada reposicionamento muda
// a bounding box e reabre a decisao. Ao inves disso, calculamos o
// placement UMA vez no `onOpen` (via `pickPlacement`) com base no
// bounding do cartao e no tamanho estimado do tooltip, e o passamos
// como `placement` fixo. `preventOverflow` ainda desliza o tooltip
// dentro da viewport sem mudar o lado.
const POPPER_OPTIONS = {
  modifiers: {
    offset: { enabled: true, offset: "0, 14" },
    flip: { enabled: false },
    preventOverflow: {
      enabled: true,
      boundariesElement: "viewport",
      padding: 12,
    },
    hide: { enabled: false },
  },
};

// Estimativa conservadora das dimensoes do tooltip para decidir onde
// cabe. Reflete o `useTooltipStyles.tooltip` (maxWidth 360) somado a
// gap e margem de seguranca.
const TOOLTIP_EST_H = 360;
const TOOLTIP_EST_W = 380;
const EDGE_MARGIN = 24;

// Decide a melhor posicao baseada no bounding do cartao. Preferencia:
// top → bottom → right → left. So avanca se a alternativa anterior nao
// tiver espaco suficiente.
const pickPlacement = (rect) => {
  if (!rect) return "top";
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const spaceAbove = rect.top - EDGE_MARGIN;
  const spaceBelow = vh - rect.bottom - EDGE_MARGIN;
  const spaceRight = vw - rect.right - EDGE_MARGIN;
  const spaceLeft = rect.left - EDGE_MARGIN;
  if (spaceAbove >= TOOLTIP_EST_H) return "top";
  if (spaceBelow >= TOOLTIP_EST_H) return "bottom";
  if (spaceRight >= TOOLTIP_EST_W) return "right";
  if (spaceLeft >= TOOLTIP_EST_W) return "left";
  return spaceBelow > spaceAbove ? "bottom" : "top";
};

// ----------------------------------------------------------------------
// Estilos do cartão
// ----------------------------------------------------------------------

const useCardStyles = makeStyles(() => ({
  // Cartão. Fundo segue o mesmo tom do cabeçalho do grupo para não
  // ficar "branco sobre branco" dentro do card do grupo.
  //
  // Atenção: NÃO aplicamos `transform: translate*` no :hover — esse
  // truque clássico deslocava a bounding box e fazia o cursor cair
  // fora do cartão nos pixels de borda, disparando um loop
  // enter/leave visível como flickering no tooltip. Trocamos por
  // apenas box-shadow (feedback visual puro, sem deslocar layout).
  card: {
    // Largura PLENAMENTE fixa nos tres eixos — ``width`` +
    // ``min/maxWidth`` + ``flex*: 0`` — para matar qualquer variacao
    // sub-pixel entre aberto/compacto. User feedback 2026-04-24:
    // "a largura esta variando ligeiramente entre a visao aberta e
    // fechada, promovendo um efeito estranho ao abrir e fechar".
    // width: INPUT_BOX_V3_MAX_WIDTH + "px",
    minWidth: INPUT_BOX_V3_MIN_WIDTH + "px",
    maxWidth: INPUT_BOX_V3_MAX_WIDTH + "px",
    // ``flex: 0 0 <w>`` explicitamente — cobre navegadores que
    // computam flex-basis de ``auto`` mesmo com width fixa. Combinado
    // com min/max iguais, a bbox do card nao pode variar.
    // flex: `0 0 ${INPUT_BOX_V3_MAX_WIDTH}px`,
    flexShrink: 0,
    flexGrow: 0,
    // Evita que qualquer filho (ex.: scoreBadge com right: -8px
    // durante a animacao de colapso do donut) estique a bbox
    // renderizada do card e cause a sensacao de "largura oscilando"
    // entre aberto/compacto.
    overflow: "hidden",
    boxSizing: "border-box",
    height: "153px",
    background: `linear-gradient(180deg, ${grayColor[19]}bb 0%, ${grayColor[10]}cc 100%)`,
    borderRadius: "6px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    margin: 0,
    padding: "14px 10px 3px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    // Duracao unica (220ms ease) compartilhada por TODAS as
    // propriedades que mudam entre compacto/expandido. Misturar
    // duracoes diferentes (ou deixar parte sem transition) produz o
    // efeito de "dois estagios" — o card animava e o conteudo
    // snapeava. Aqui o card lidera com height/padding; demais
    // elementos abaixo replicam o mesmo "220ms ease" para que tudo
    // se mova como uma coisa so.
    transition: "height 220ms ease, padding 220ms ease, box-shadow 150ms ease",
    "&:hover": {
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
  },
  // Overlay de "solte para enviar" — aparece sobre o card quando um
  // arquivo esta sendo arrastado por cima (drag & drop direto na UI
  // principal, sem abrir o drawer). ``pointer-events: none`` e'
  // essencial: o handler de drop vive no proprio card e o overlay nao
  // pode roubar eventos de dragleave (senao ele pisca).
  dropOverlay: {
    position: "absolute",
    inset: 3,
    zIndex: 25,
    borderRadius: 6,
    background: "rgba(255, 255, 255, 0.92)",
    border: `2px dashed ${grayColor[3]}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    pointerEvents: "none",
    color: grayColor[1],
  },
  dropOverlayCompact: {
    flexDirection: "row",
    gap: 8,
  },
  dropIcon: {
    color: grayColor[3],
  },
  dropText: {
    fontSize: 11,
    fontWeight: 600,
    textAlign: "center",
    lineHeight: 1.15,
    padding: "0 6px",
  },
  // Estado compacto: o mesmo card, mas com o donut + linha de
  // responsavel colapsados. Preserva o wrapper e os elementos no
  // DOM — alteramos apenas altura/opacidade/margens — para permitir
  // transicao suave entre "grupo aberto" e "grupo fechado" sem que
  // os input boxes "desmontem e remontem".
  cardCompact: {
    height: "47px",
    padding: "5px 10px 4px",
  },
  collapsible: {
    // Wrapper da linha de responsavel. ``maxHeight + opacity +
    // margin`` precisam animar em sincronia com o resto — sem
    // ``transition`` aqui, o ``responsible`` snapeava enquanto o
    // ``card`` ainda crescia/encolhia.
    overflow: "hidden",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "max-height 220ms ease, opacity 220ms ease, margin 220ms ease",
  },
  collapsibleOpen: {
    maxHeight: "22px",
    opacity: 1,
  },
  collapsibleClosed: {
    maxHeight: 0,
    opacity: 0,
    margin: 0,
  },
  markerRail: {
    position: "absolute",
    top: "9px",
    right: "9px",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    zIndex: 2,
    transition: "opacity 220ms ease",
  },
  markerRailHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
  markerIcon: {
    width: "15px",
    height: "15px",
    borderRadius: "50%",
    color: grayColor[1],
    background: whiteColor,
    boxShadow: "0 0 0 1px rgba(60, 72, 88, 0.14)",
    padding: "2px",
  },
  markerIconAuto: {
    color: successColor[3],
  },
  markerIconSemi: {
    color: grayColor[2],
  },
  donutWrap: {
    position: "relative",
    width: "68px",
    height: "68px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginBottom: "14px",
  },
  donutCenter: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  scoreBadge: {
    position: "absolute",
    bottom: "-4px",
    right: "-8px",
    minWidth: "34px",
    padding: "2px 6px",
    borderRadius: "999px",
    fontSize: "11px",
    fontWeight: 700,
    color: whiteColor,
    textAlign: "center",
    border: "2px solid " + whiteColor,
    lineHeight: 1.2,
  },
  // Onda 1.3: efeito ripple horizontal — mesmo perfil de
  // ``StagePips.inFlight`` e do chip "Processando" do header.
  // ``backgroundSize 200% 100%`` casa com o gradiente
  // ``base -> luz -> base`` definido em ``RUNNING_STRIPE_BG``; a
  // animacao desliza ``backgroundPosition`` para a luz cruzar a
  // largura do badge. Duracao 1400ms = ``IN_FLIGHT_DURATION_MS`` em
  // ``StagePips.js`` (mantemos os 3 elementos sincronizados).
  scoreBadgeRunning: {
    backgroundSize: "200% 100%",
    animation: "$inputBoxRunningStripeMove 1400ms ease-in-out infinite",
  },
  "@keyframes inputBoxRunningStripeMove": {
    "0%": { backgroundPosition: "200% 0" },
    "100%": { backgroundPosition: "-200% 0" },
  },
  mediaSlot: {
    width: "100%",
    height: "82px",
    marginBottom: 0,
    position: "relative",
    overflow: "hidden",
    borderBottom: "1px solid transparent",
    boxSizing: "border-box",
    transition:
      "height 220ms ease, margin-bottom 220ms ease, border-bottom-color 220ms ease",
  },
  mediaSlotCompact: {
    height: "17px",
    marginBottom: "6px",
    borderBottomColor: grayColor[5],
  },
  mediaLayer: {
    position: "absolute",
    inset: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    opacity: 1,
    pointerEvents: "none",
    transform: "scale(1)",
    transformOrigin: "center top",
    // Fade do donut (e do pip no outro layer) propositalmente MAIS
    // LONGO (300ms) e ``ease-in-out`` para criar um efeito de fade
    // visivelmente suave — entra/sai do nada em vez de "snap" com o
    // height do slot. O ``scale(0.94)`` no estado hidden da uma
    // sensacao sutil de profundidade (o donut se aproxima quando
    // aparece, recua quando some), reforcando o fade ao olho.
    transition: "opacity 300ms ease-in-out, transform 300ms ease-in-out",
  },
  mediaLayerHidden: {
    opacity: 0,
    transform: "scale(0.94)",
  },
  // Altura fixa reservando sempre 2 linhas (mesmo em nomes curtos),
  // para que a quebra de linha em nomes longos não faça o cartão
  // crescer mais que os irmãos.
  //
  // IMPORTANTE: ``WebkitLineClamp`` fica fixo em 2 em ambos os
  // modos — propriedade nao-interpolavel, mudar dispara snap. No
  // compacto a 2a linha eh recortada pelo ``overflow: hidden`` +
  // ``height: 15px``. Asim height/margin sao as UNICAS coisas que
  // mudam no nome, e ambas animam suavemente.
  name: {
    fontSize: "12px",
    fontWeight: 500,
    color: grayColor[2],
    textAlign: "center",
    lineHeight: 1.25,
    height: "30px",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    // ``width: 100%`` garante largura constante do bloco do nome em
    // qualquer modo (aberto / compacto). Sem isso, block + ellipsis
    // no compact caia para largura intrinseca do texto e produzia
    // reflow de 1-2px no card ao expandir/retrair.
    // width: "100%",
    maxWidth: `${INPUT_BOX_V3_MAX_WIDTH - 16}px`,
    marginBottom: "2px",
    transition: "height 220ms ease, margin-bottom 220ms ease",
    letterSpacing: "-0.5px !important",
  },
  nameCompact: {
    height: "15px",
    marginBottom: 0,
    // No compacto reservamos UMA linha so. Trocamos o ``-webkit-box``
    // com ``WebkitLineClamp: 2`` (herdado de ``name``) por
    // ``block`` + ``white-space: nowrap`` + ``text-overflow: ellipsis``
    // para que nomes longos terminem com "..." em vez de simplesmente
    // serem cortados pela altura. Esse era o bug que fazia
    // ``Ruteo Merchandisers Cordoba`` parecer ``Ruteo Merchandisers``
    // (consolidado) na grade compacta. O snap visual entre modos
    // (mudanca de ``display``/``line-clamp``) e' imperceptivel porque
    // ocorre dentro da transicao de ``height`` de 220ms.
    display: "block",
    WebkitLineClamp: 1,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  responsible: {
    fontSize: "11px",
    color: grayColor[1],
    display: "flex",
    alignItems: "center",
    gap: "3px",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: "6px",
  },
  // Contador "+N" quando há múltiplos responsáveis. Visualmente
  // discreto (tom mais claro que o nome) para não competir com ele.
  respExtra: {
    fontSize: "10px",
    fontWeight: 600,
    color: grayColor[1],
    flexShrink: 0,
    marginLeft: "2px",
  },
}));

// ----------------------------------------------------------------------
// Componente principal
// ----------------------------------------------------------------------

const InputBoxV3 = ({
  input,
  onClick,
  displayResponsible,
  compact,
  onInputFileDrop,
  onDropReject,
}) => {
  const c = useCardStyles();
  const tooltipClasses = useTooltipStyles();
  const { i18n, t } = useTranslation();
  const lang = i18n.language;

  // Drag & drop direto no card (UI principal): so e' "droppable" quando
  // o pai fornece ``onInputFileDrop`` — que ja vem gateado por permissao
  // de upload. Sem o callback, os handlers ficam inertes.
  const droppable = typeof onInputFileDrop === "function";
  const handleDroppedFile = useCallback(
    (file) => onInputFileDrop(input, file),
    [onInputFileDrop, input],
  );
  const { isOver: isDragOver, dropHandlers } = useFileDropZone({
    onFile: handleDroppedFile,
    onReject: onDropReject,
    disabled: !droppable,
  });

  const state = classifyInputState(input);
  const color = STATE_COLORS[state];
  // ``processing`` = algo acontecendo neste instante (upload/parser/
  // xlink rodando) e deve usar cinza animado. ``in_progress`` fica
  // reservado para "Aguardando para avancar" (verde claro solido).
  const isRunningVisual = state === "processing";
  // D-MTX-1: overlay de listras animadas sobre cor base do donut/badge
  // quando o input tem xlinks ainda em pre-execucao (sem CSL ou em
  // pending/blocked/running/stale). Pip mantem o veredito agregado e
  // ganha a sinalizacao "tem coisa rodando agora".
  const { hasInFlightOverlay } = classifyInputPip(input);
  const score = deriveQualityScore(input);
  const scoreStr = score == null ? "n/a" : score + "%";
  // Regra do preenchimento do donut (alinhada ao badge de estado):
  //   - ``not_sent`` (cinza): aro 100% cinza — o donut *e* o anel de
  //     fundo. Nao ha arco colorido porque nao ha informacao para
  //     reportar. Mantemos ``donutProgress = 0`` para respeitar a
  //     semantica "sem dado".
  //   - qualquer outro estado: enchemos o donut COMPLETAMENTE na cor
  //     do badge (100%). Enquanto o backend nao expoe um
  //     ``quality_score`` real, o donut comunica o estado atraves da
  //     cor — e nao uma cobertura de cruzamento fake. Se ``score``
  //     vier populado no futuro, ele dita a fracao exibida; ate la,
  //     o estado manda.
  const donutProgress =
    score != null ? score / 100 : state === "not_sent" ? 0 : 1;
  const nature = deriveNature(input);
  const displayName =
    (input?.input_name && input.input_name[lang]) ||
    input?.input_name?.pt ||
    input?.meta_name ||
    "";
  const respList = responsibleList(input);
  // `displayResponsible` é um override opcional do nome a destacar como
  // primário no cartão. Usado pelo modo "Por responsável" para que, quando
  // um input com múltiplos donos aparece no bloco da Maria, o cartão
  // mostre "Maria" e não o primeiro da lista canônica. Se o nome passado
  // não constar na lista, o override é ignorado (fallback seguro).
  const overrideIdx =
    displayResponsible != null
      ? respList.findIndex((r) => (r?.name || "") === displayResponsible)
      : -1;
  const primaryResp =
    overrideIdx >= 0
      ? respList[overrideIdx]?.name || ""
      : respList.length > 0
        ? respList[0]?.name || ""
        : "";
  const extraRespCount = Math.max(0, respList.length - 1);
  const isMine = !!input?.is_mine;
  // Rev. 2026-05-28 — ``semi_fixed`` foi removido. O marker "Repeat"
  // (icone de reuso) agora deriva exclusivamente da flag ``reuse_auto``
  // do input (configurada via UI na AboutPane). So aparece para
  // inputs ``monthly`` ja que para singleton/yearly/carry_forward a
  // propria volatility implica cobertura multi-mes.
  const showReuseMarker = !!(
    input?.volatility === "monthly" && input?.reuse_auto
  );
  const isSingletonInput = isSingleton(input?.volatility);
  const isCarryForwardInput = isCarryForward(input?.volatility);
  const isYearlyInput = isYearly(input?.volatility);
  const isOptionalInput = !!input?.optional;

  // Placement dinamico: calculado uma vez no `onOpen` a partir do
  // bounding do cartao. Com flip desligado no popper, essa escolha e
  // definitiva durante a exibicao do tooltip — sem flickering.
  const cardRef = useRef(null);
  const [placement, setPlacement] = useState("top");
  // Tooltip controlado: precisamos forcar o fechamento ao clicar no
  // cartao (abre o drawer). Sem isso, o tooltip pode permanecer
  // visivel por cima do drawer ate que o mouse saia da area —
  // confunde porque ``interactive`` mantem o popper aberto enquanto
  // o cursor estiver sobre ele.
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const handleOpen = useCallback(() => {
    const rect = cardRef.current?.getBoundingClientRect();
    setPlacement(pickPlacement(rect));
    setTooltipOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setTooltipOpen(false);
  }, []);
  const handleCardClick = useCallback(
    (event) => {
      setTooltipOpen(false);
      if (onClick) onClick(input, event);
    },
    [onClick, input],
  );

  return (
    <Tooltip
      title={<TooltipBody input={input} />}
      arrow
      placement={placement}
      classes={{ tooltip: tooltipClasses.tooltip, arrow: tooltipClasses.arrow }}
      interactive
      open={tooltipOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      // Debounce da abertura: impede multiplas impressoes quando o
      // mouse passa rapido por varios cartoes. `enterNextDelay` anula
      // o atalho do MUI (que reabre quase instantaneo ao ir de um
      // cartao para um cartao vizinho).
      enterDelay={1000}
      enterNextDelay={1000}
      leaveDelay={40}
      PopperProps={{ popperOptions: POPPER_OPTIONS }}
    >
      <div
        ref={cardRef}
        className={`${c.card} ${compact ? c.cardCompact : ""}`}
        onClick={onClick ? handleCardClick : undefined}
        {...(droppable ? dropHandlers : {})}
      >
        {isDragOver && (
          <div
            className={`${c.dropOverlay} ${
              compact ? c.dropOverlayCompact : ""
            }`}
            aria-hidden="true"
          >
            <CloudUpload
              className={c.dropIcon}
              style={{ fontSize: compact ? 20 : 28 }}
            />
            {!compact && (
              <span className={c.dropText}>{t("inputs_v3.drop.box_hint")}</span>
            )}
          </div>
        )}
        {(isMine ||
          showReuseMarker ||
          isSingletonInput ||
          isCarryForwardInput ||
          isYearlyInput ||
          isOptionalInput) && (
          <div
            className={`${c.markerRail} ${compact ? c.markerRailHidden : ""}`}
            aria-label={t(
              "inputs_v3.input_tooltip.markers_aria_label",
              "Marcadores do input",
            )}
            aria-hidden={compact}
          >
            {isMine && (
              <PersonOutline
                className={c.markerIcon}
                titleAccess={t(
                  "modeling_inputs_v3.legend.marker_mine",
                  "Sob sua responsabilidade",
                )}
              />
            )}
            {showReuseMarker && (
              <Repeat
                className={`${c.markerIcon} ${c.markerIconSemi}`}
                titleAccess={t("modeling_inputs_v3.legend.marker_reuse_auto")}
              />
            )}
            {isSingletonInput && (
              <FlashOn
                className={`${c.markerIcon} ${c.markerIconAuto}`}
                titleAccess={t("modeling_inputs_v3.legend.marker_singleton")}
              />
            )}
            {isCarryForwardInput && (
              <TrendingFlat
                className={`${c.markerIcon} ${c.markerIconAuto}`}
                titleAccess={t(
                  "modeling_inputs_v3.legend.marker_carry_forward",
                )}
              />
            )}
            {isYearlyInput && (
              <Event
                className={c.markerIcon}
                titleAccess={t("modeling_inputs_v3.legend.marker_yearly")}
              />
            )}
            {isOptionalInput && (
              <HelpOutline
                className={c.markerIcon}
                titleAccess={t(
                  "modeling_inputs_v3.legend.marker_optional",
                  "Opcional",
                )}
              />
            )}
          </div>
        )}
        <div className={`${c.mediaSlot} ${compact ? c.mediaSlotCompact : ""}`}>
          <div
            className={`${c.mediaLayer} ${compact ? c.mediaLayerHidden : ""}`}
            aria-hidden={compact}
          >
            <div className={c.donutWrap}>
              <Donut
                progress={donutProgress}
                color={color}
                size={68}
                dashed={false}
                striped={isRunningVisual}
                inFlight={hasInFlightOverlay}
              />
              <div className={c.donutCenter}>
                {natureIconEl(nature, {
                  width: 26,
                  height: 26,
                  color: grayColor[1],
                })}
              </div>
              <div
                className={`${c.scoreBadge} ${
                  isRunningVisual ? c.scoreBadgeRunning : ""
                }`}
                style={{
                  // ``STATE_BACKGROUNDS`` cobre o caso especial
                  // ``pending_decision`` (hachurado warning+danger);
                  // demais buckets caem na cor solida (igual a
                  // ``STATE_COLORS``). Mantemos o ramo ``running`` com
                  // o stripe verde (animado), independente.
                  background: isRunningVisual
                    ? RUNNING_STRIPE_BG
                    : STATE_BACKGROUNDS[state],
                }}
              >
                {scoreStr}
              </div>
            </div>
          </div>
          <div
            className={`${c.mediaLayer} ${compact ? "" : c.mediaLayerHidden}`}
            aria-hidden={!compact}
          >
            {/*
              Pip unico (variante "inputSingle"): substitui os 3 pips
              por uma cor sintese do agregado do input. No modo expandido
              o donut + badge ja carregam essa leitura visual.
            */}
            <StagePips
              variant="inputSingle"
              states={[inputSinglePipState(input)]}
              inFlight={hasInFlightOverlay}
            />
          </div>
        </div>
        <div className={`${c.name} ${compact ? c.nameCompact : ""}`}>
          {displayName}
        </div>
        <div
          className={`${c.collapsible} ${compact ? c.collapsibleClosed : c.collapsibleOpen}`}
          aria-hidden={compact}
        >
          <div
            className={c.responsible}
            style={
              !primaryResp ? { fontStyle: "italic", color: grayColor[1] } : {}
            }
          >
            <PersonOutline style={{ fontSize: 12 }} />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {primaryResp || "Sem responsável"}
            </span>
            {extraRespCount > 0 && (
              <span className={c.respExtra}>+{extraRespCount}</span>
            )}
          </div>
        </div>
      </div>
    </Tooltip>
  );
};

InputBoxV3.propTypes = {
  input: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  // Nome a destacar como primário, sobrepondo o primeiro da lista
  // canônica. Usado pelo modo "Por responsável" em InputsV3.
  displayResponsible: PropTypes.string,
  // Modo compacto — usado quando o card do grupo esta "fechado" no
  // accordion: colapsa donut e linha de responsavel, preservando
  // apenas nome (single-line + ellipsis) e os 3 traços de progresso.
  // Mantem o tooltip rico funcionando.
  compact: PropTypes.bool,
  // Drag & drop: chamado com ``(input, File)`` quando um arquivo e'
  // solto sobre o card. Quando ausente, o card NAO e' droppable (e' o
  // ponto de gate por permissao — o pai so passa quando o usuario pode
  // enviar). ``onDropReject`` recebe a chave do motivo (pasta/nao-arquivo).
  onInputFileDrop: PropTypes.func,
  onDropReject: PropTypes.func,
};

export default InputBoxV3;
